import store from "../../store";
import {
  IAuthenticationService, Authenticate,
} from "../../domain/authentication";
import { Department } from "../../domain/department";
import { IAuthenticationRepository } from "./IAuthenticationRepository";
import { IConfiguration } from "../../domain/configuration";

export class AuthenticationService implements IAuthenticationService {
  constructor(private readonly _repository: IAuthenticationRepository) {
  }

  async login(id: string, password: string, departmentId: string, hostName: string, departmentGroupId: string, referenceableDepartmentGroupIds: string[]): Promise<void> {
    try {
      const isDepartmentGroupSpecified = !!departmentGroupId;
      const isDepartmentGroupScopeEnable = this.isDepartmentGroupScopeEnable(isDepartmentGroupSpecified);
      const result = await this._repository.getAuthentication(id, password, hostName ? hostName : store.getters.hostName, departmentId, departmentGroupId, referenceableDepartmentGroupIds, isDepartmentGroupScopeEnable);
      const department = this.getLoginDepartment(result, isDepartmentGroupSpecified);
      this.updateStore(result, department, hostName, isDepartmentGroupScopeEnable);
    } catch (e) {
      this.clearStore();
      throw e;
    }
  }

  async authenticate(id: string, password: string): Promise<void> {
    await this._repository.getAuthentication(id, password, store.getters.hostName, null, null, null, false);
  }

  private updateStore(source: Authenticate, department: Department, hostName: string, isDepartmentGroupScopeEnable: boolean): void {
    store.commit("setToken", source.token);
    console.debug(store.getters.token);
    store.commit("setUser", source.user);
    console.debug(store.getters.user);
    store.commit("setDepartment", department);
    console.debug(store.getters.department);
    store.commit("setUserAuthority", source.userAuthority);
    console.debug(store.getters.userAuthority);
    if (hostName) {
      store.commit("setHostName", hostName);
    }
    console.debug("hostName: " + store.getters.hostName);
    store.commit("setUserDepartments", source.departments);
    console.debug(store.getters.userDepartments);
    store.commit("setOrderUser", source.orderUser);
    console.debug(store.getters.orderUser);
    store.commit("setAttendingUser", source.attendingUser);
    console.debug(store.getters.attendingUser);

    store.commit("setExtraConfiguration", source.extraConfiguration);
    console.debug(store.getters.extraConfiguration);

    store.commit("setDepartmentGroup", source.departmentGroup);
    console.debug(store.getters.departmentGroup);
    store.commit("setReferenceableDepartmentGroups", source.referenceableDepartmentGroups);
    console.debug(store.getters.referenceableDepartmentGroups);
    store.commit("setIsDepartmentGroupScopeEnable", isDepartmentGroupScopeEnable);
    console.debug(store.getters.isDepartmentGroupScopeEnable);
  }

  private clearStore(): void {
    // storeを全て削除すると、store参照してるリアクティブ要素が更新されるので、store参照実装全てにnullガードが必要.
    // 今後のメンテを考えても手がかかり過ぎるので、ログアウト時に消すのはtokenだけにする.
    // (tokenをView側が見る事はない設計なのでtokenが消えてるのは問題ない.むしろ見てたら見ないように直すべし.)
    // リダイレクトした先でちゃんと消す等した方がいいかもしれないが、問題が顕在化しない限りはこれで.
    store.commit("setToken", null);
  }

  async loginAutomatically(id: string, departmentId: string, hostName: string, orderUserId: string, attendingUserId: string, departmentGroupId: string, referenceableDepartmentGroupIds: string[]): Promise<void> {
    try {
      const isDepartmentGroupSpecified = !!departmentGroupId;
      const isDepartmentGroupScopeEnable = this.isDepartmentGroupScopeEnable(isDepartmentGroupSpecified);
      const result = await this._repository.getAuthenticationWithNoPassword(id, hostName ? hostName : store.getters.hostName, departmentId, orderUserId, attendingUserId, departmentGroupId, referenceableDepartmentGroupIds, isDepartmentGroupScopeEnable);
      const department = this.getLoginDepartment(result, isDepartmentGroupSpecified);
      this.updateStore(result, department, hostName, isDepartmentGroupScopeEnable);
    } catch (e) {
      this.clearStore();
      throw e;
    }
  }

  /**
   * ログイン診療科の優先順位
   * 1.引数で指定されている診療科
   * 2.所属診療科が空の場合は自動選択しない（設定項目あり）
   * 3.所属診療科が1つの場合はその診療科
   * 4.前回選択したログイン診療科
   * 5.主科
   */
  private getLoginDepartment(auth: Authenticate, isDepartmentGroupSpecified: boolean): Department {
    if (!auth) { return null; }
    if (auth.loginDepartment) {
      if (!this.isValidDepartment(auth, auth.loginDepartment, isDepartmentGroupSpecified)) {
        // 自動選択するべきではないので選択してもらう
        return null;
      }
      return auth.loginDepartment;
    }
    if (!this.shouldSelectDepartmentAutomatically()) {
      // 所属診療科が1つのみの場合は、選択ダイアログを表示する処理の中で自動選択する処理があるので選択ダイアログは表示されない
      return null;
    }
    if ((!auth.departments || auth.departments.length === 0) && this.shouldSkipDepartmentAutoSelectionIfDepartmentsIsEmpty()) {
      // 愛大では所属診療科が無い職員は必ず選択ダイアログを表示して選択させたいとの要望あり、設定追加で対応している
      return null;
    }
    if (auth.departments && auth.departments.length === 1 && this.isValidDepartment(auth, auth.departments[0], isDepartmentGroupSpecified)) {
      return auth.departments[0];
    }
    const previousSelectedDepartment = this.getPreviousSelectedDepartment(auth);
    if (this.isValidDepartment(auth, previousSelectedDepartment, isDepartmentGroupSpecified)) {
      return previousSelectedDepartment;
    }
    return this.isValidDepartment(auth, auth.mainDepartment, isDepartmentGroupSpecified)
      ? auth.mainDepartment
      : null;
  }

  private shouldSelectDepartmentAutomatically(): boolean {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.authentication.login.shouldSelectDepartmentAutomatically;
  }

  private shouldSkipDepartmentAutoSelectionIfDepartmentsIsEmpty(): boolean {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.authentication.login.shouldSkipDepartmentAutoSelectionIfDepartmentsIsEmpty;
  }

  private isValidDepartment(auth: Authenticate, department: Department, isDepartmentGroupSpecified: boolean): boolean {
    if (!department || department.isDeleted) { return false; }
    if (!this.isDepartmentGroupScopeEnable(isDepartmentGroupSpecified)) { return true; }
    return auth.departmentGroup && auth.departmentGroup.departments.some(d => d.id === department.id);
  }

  private isDepartmentGroupScopeEnable(isDepartmentGroupSpecified: boolean): boolean {
    const configuration = store.getters.configuration as IConfiguration;
    return configuration.behavior.departmentGroup.scope.isEnable && isDepartmentGroupSpecified;
  }

  private getPreviousSelectedDepartment(auth: Authenticate): Department {
    // 所属診療科が無い場合は結局はダイアログにすべて表示されて選択しなおすことになるので、前回選択した診療科を返却する
    if (!auth.departments || auth.departments.length === 0) { return auth.previousSelectedDepartment; }
    return (auth.previousSelectedDepartment && auth.departments && auth.departments.some(d => d.id === auth.previousSelectedDepartment.id))
      ? auth.previousSelectedDepartment
      : null;
  }

  async logout(): Promise<void> {
    try {
      await this._repository.discardAuthentication(store.getters.user.id, store.getters.token);
      this.clearStore();
    } catch (e) {
      this.clearStore();
      throw e;
    }
  }
}
